import { z } from 'zod';
import {
  UuidSchema,
  DateTimeSchema,
  CurrencyCodeSchema,
  PositiveNumberSchema,
  PaginationSchema,
  WeightSchema
} from './common.schema';
import { UserSchema } from './user.schema';
import { CategorySchema } from './category.schema';

// Expense schemas
export const ExpenseSchema = z.object({
  id: UuidSchema,
  group_id: UuidSchema,
  category_id: UuidSchema,
  description: z.string().nullable().optional(),
  amount: z.union([PositiveNumberSchema, z.string()]).transform((val) =>
    typeof val === 'string' ? parseFloat(val) : val
  ),
  currency_code: CurrencyCodeSchema,
  paid_by: UuidSchema,
  created_by: UuidSchema.optional(),
  created_at: DateTimeSchema,
  user: UserSchema.partial().optional(),
  category: CategorySchema.partial(),
  weights: z.array(WeightSchema).optional(),
  synced: z.boolean().optional(), // For optimistic updates
});

export const ExpenseSplitSchema = z.object({
  id: UuidSchema,
  expense_id: UuidSchema,
  user_id: UuidSchema,
  weight: z.number().min(0),
});

// Request schemas
export const CreateExpenseRequestSchema = z.object({
  group: UuidSchema,
  category: UuidSchema,
  description: z.string().optional(),
  amount: PositiveNumberSchema,
  currency_code: CurrencyCodeSchema,
  date: z.date().optional(),
  funding_pool_id: UuidSchema.optional(),
  use_group_pool: z.boolean().optional(),
});

// Response schemas
export const PaginatedExpensesSchema = z.object({
  expenses: z.array(ExpenseSchema),
  pagination: PaginationSchema,
});

export const ExpenseResponseSchema = z.object({
  expense: ExpenseSchema,
});

// Split calculation schemas
export const WeightInputSchema = z.object({
  userId: UuidSchema,
  weight: z.number().min(0),
});

export const SplitResultSchema = z.object({
  userId: UuidSchema,
  amount: z.number(),
});

// Client-side expense params
export const CreateExpenseParamsSchema = z.object({
  groupId: UuidSchema,
  categoryId: UuidSchema,
  description: z.string().optional(),
  amount: PositiveNumberSchema,
  currency: CurrencyCodeSchema,
  date: z.date().optional(),
  fundingPoolId: UuidSchema.optional(),
  useGroupPool: z.boolean().optional(),
});

// Derived types
export type Expense = z.infer<typeof ExpenseSchema>;
export type ExpenseSplit = z.infer<typeof ExpenseSplitSchema>;
export type CreateExpenseRequest = z.infer<typeof CreateExpenseRequestSchema>;
export type PaginatedExpenses = z.infer<typeof PaginatedExpensesSchema>;
export type ExpenseResponse = z.infer<typeof ExpenseResponseSchema>;
export type WeightInput = z.infer<typeof WeightInputSchema>;
export type SplitResult = z.infer<typeof SplitResultSchema>;
export type CreateExpenseParams = z.infer<typeof CreateExpenseParamsSchema>;
